import React from "react";
import { Autocomplete, Box, TextField, Select, MenuItem } from "@mui/material";
import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import { QueryFieldSelector } from "components/queries/queryBuilder/dialogParts/QueryFieldSelector";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { QueryField, QueryInfo, fieldIsKeyword, getRecordsField, getSocialUniverseField } from "system/query/QueryInfo";
import { Agg, aggOperatorsByType, AggOptions, OpOption } from "system/query/Aggs";
import { QueryFixedKeywordInput } from "components/queries/queryBuilder/dialogParts/QueryFixedKeywordInput";
import { QueryDynamicOptionsInput } from "components/queries/queryBuilder/dialogParts/QueryDynamicKeywordInput";

export interface LeafAggSelectorProps {
  queryInfo: QueryInfo;

  allowedFieldTypes: string[];

  aggs?: Agg;
  setAggs: (newAggs: Agg | null, label?: string) => void;

  aggsLabel?: string;
  setAggsLabel?: (newAggsLabel: string) => void;

  disableKeywordFilter?: boolean;
  fixedOperator?: string; // (e.g., "sum", "avg", "min", "max")

  customFieldLabel?: string;
}

export default function LeafAggSelector(props: LeafAggSelectorProps): JSX.Element {
  const { i18n } = useTranslation();

  const fields: QueryField[] = props.queryInfo?.fields;
  const [selectedField, setSelectedField] = useState<QueryField | undefined>();
  const [opOptions, setOpOptions] = useState<OpOption[] | null>(null);
  const [opInputValue, setOpInputValue] = useState<string>('');
  const [autocompleteLabel, setAutocompleteLabel] = useState<string>("");

  useEffect(() => {
    if (props.aggs?.field) {
      setSelectedField(fields?.find((field: QueryField) => field.id === props.aggs?.field));
    } else if(props.aggs?.op === "social_universe" && props.queryInfo.supported_aggregation_ops.includes("social_universe")) {
      setSelectedField(getSocialUniverseField());
    } else {
      setSelectedField(getRecordsField());
    }
  }, [props.aggs?.field, fields, props.queryInfo.supported_aggregation_ops]);

  useEffect(() => {
    if (!selectedField?.type || selectedField.id === "__count__" || selectedField.id === "social_universe" || fieldIsKeyword(selectedField)) {
      setOpOptions(null);
      return;
    }
    if (props.fixedOperator) {
      setOpOptions([{ id: props.fixedOperator, label: i18n.t(props.fixedOperator) }]);
    } else {
      setOpOptions(aggOperatorsByType(selectedField, i18n.t, props.queryInfo.supported_aggregation_ops));
    }
  }, [selectedField, selectedField?.type]);

  const buildAggs = (options:AggOptions) => {
    const fieldId = options.field || props.aggs?.field;
    const field = fields.find((field: QueryField) => field.id === fieldId);
    const op = options.op || props.fixedOperator;

    if (field?.id === "__count__") {
      return null;
    }

    if (field?.id === "social_universe") {
      return {
        op: "social_universe"
      };
    }

    const aggs:Agg = {
      field: fieldId,
      op: op || "avg"
    };
    if (options.include_null) {
      aggs.include_null = options.include_null;
    }

    if (options.label) {
      aggs.label = options.label;
    }
    if (options.labels) {
      aggs.labels = options.labels;
    }

    if (options.op === null) {
      delete aggs.op;
    }

    if (options?.filter || aggs.filter) {
      aggs.filter = options.filter as string[];
    } else if (aggs.filter === null) {
      delete aggs.filter;
    }

    return aggs;
  }

  const updateAggs = (options:AggOptions, queryField?: QueryField) => {
    const aggs = buildAggs(options);
    props.setAggs(aggs, queryField?.label || selectedField?.label);
  }

  const isBoolean = selectedField?.type === "boolean";
  const selectedBooleanValue = props.aggs?.filter?.[0] === "true";

  return (
    <Box>
      <QueryFieldSelector
        fields={fields}
        requireAggSupported={true}
        allowedFieldTypes={props.allowedFieldTypes}
        selectedFieldId={selectedField?.id || ""}
        setSelectedFieldId={(newFieldId:string) => {
          const newSelectedField = fields?.find((field: QueryField) => field.id === newFieldId);
          setSelectedField(newSelectedField);
          const aggOptions:Agg = { field: newFieldId };
          if (props.setAggsLabel) {
            props.setAggsLabel(newSelectedField?.label || "");
          }
          if (newSelectedField && fieldIsKeyword(newSelectedField)) {
            aggOptions.op = "keyword";
          } else if (props.aggs?.op === "keyword" && newSelectedField && !fieldIsKeyword(newSelectedField)) {
            aggOptions.op = "avg";
          }
          if (newSelectedField?.options?.length && !props.disableKeywordFilter) {
            // When a new field is selected also set a default selected option.
            const defaultOption = newSelectedField.options[0];
            if (defaultOption.id) {
              aggOptions.filter = [defaultOption.id];
              if (defaultOption.label) {
                aggOptions.labels = [defaultOption.label];
              }
            }
          }
          updateAggs(aggOptions, newSelectedField);
        }}
      />
      {isBoolean && (<>
        <SimpleFormLabel>{i18n.t("value")}</SimpleFormLabel>
        <Select
          sx={{ minWidth: "150px" }}
          value={selectedBooleanValue ? "true" : "false"}
          onChange={(event) => {
            const newValue = event.target.value;
            updateAggs({
              filter: [newValue === "true" ? "true" : "false"]
            });
          }}
        >
          <MenuItem value="true">{i18n.t("true")}</MenuItem>
          <MenuItem value="false">{i18n.t("false")}</MenuItem>
        </Select>
      </>)}
      {!isBoolean && !props.fixedOperator && opOptions &&
        <>
          <SimpleFormLabel>{i18n.t("filterable-table.operator")}</SimpleFormLabel>
          <Autocomplete
            sx={{minWidth: "150px"}}
            options={opOptions}
            inputValue={opInputValue}
            onInputChange={(_, newInputValue) => {
              setOpInputValue(newInputValue);
            }}
            value={opOptions.find((op: OpOption) => op.id === props.aggs?.op) || null}
            onChange={(event: React.SyntheticEvent, newValue: OpOption | null) => {
              updateAggs({
                op: newValue?.id
              });
            }}
            renderInput={(params) => <TextField autoComplete="off" {...params} />}
          />

        </>
      }
      {!isBoolean && selectedField?.options && !props.disableKeywordFilter &&
        <>
          <SimpleFormLabel>{props.customFieldLabel || i18n.t("field")}</SimpleFormLabel>
          <QueryFixedKeywordInput
            enforceSelection={true}
            queryInfo={props.queryInfo}
            selectedField={selectedField}
            value={props.aggs?.filter?.[0]}
            setValue={(newValue: string | number | boolean | null | undefined, label?: string | undefined) => {
              if (newValue !== undefined) {
                const newAggs = {
                  op: "keyword",
                  filter: [newValue as string]
                } as Agg;
                if (label) {
                  newAggs.labels = [label];
                }
                if (newValue === null) {
                  newAggs.include_null = true;
                }
                updateAggs(newAggs);
              }
            }}
          />
        </>
      }
      {!isBoolean && selectedField && fieldIsKeyword(selectedField) && !selectedField.options && !props.disableKeywordFilter &&
        <>
          <SimpleFormLabel>{i18n.t("field")}</SimpleFormLabel>
          <QueryDynamicOptionsInput
            queryInfo={props.queryInfo}
            selectedField={selectedField}
            label={autocompleteLabel}
            setLabel={setAutocompleteLabel}
            value={props.aggs?.filter?.[0]}
            setValue={(newValue: string | number | boolean | null | undefined, label?: string | undefined) => {
              if (newValue) {
                const newAggs = {
                  op: "keyword",
                  filter: [newValue as string]
                } as Agg;
                if (label) {
                  newAggs.labels = [label];
                }
                updateAggs(newAggs);
              }
            }}
          />
        </>
      }
    </Box>
  );
}
